// ============================================
// Hemologix Master Formulary — Tier 1 Supplements
// 29 Seed Products (catalogNumber 1-29)
// ============================================

import type { Product } from '../../types/formulary';

export const SEED_SUPPLEMENTS: Product[] = [
  // ─────────────────────────────────────────────
  // 1. Multivitamin
  // ─────────────────────────────────────────────
  {
    id: 'supp-multivitamin',
    catalogNumber: 1,
    name: 'Multivitamin',
    ingredients: 'Methylated B-vitamins, chelated minerals, vitamin A, C, D3, E, K2, zinc, selenium, chromium',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '2 caps daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'general_nutrition',
        condition: 'any_suboptimal',
        description: 'Multiple micronutrient deficiencies detected',
      },
    ],
    targetBiomarkers: ['vitamin_d', 'vitamin_b12', 'folate', 'zinc', 'selenium', 'iron'],
    targetGoals: ['energy', 'immune', 'longevity'],
    pairsWith: ['supp-omega-3-fish-oil', 'supp-vitamin-d3-k2', 'supp-probiotic'],

    contraindications: ['hypervitaminosis_A', 'hemochromatosis_if_iron_containing'],
    drugInteractions: ['tetracycline_antibiotics_reduce_absorption', 'levothyroxine_separate_by_4hrs'],
    allergenFlags: ['soy_possible', 'gelatin_capsule'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['comprehensive_metabolic_panel_annually'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'A comprehensive multivitamin addresses widespread subclinical deficiencies. The COSMOS trial (2022) showed modest benefits for cognition and cardiovascular outcomes in older adults.',
    citations: [
      'Baker LD et al. Effects of cocoa extract and multivitamin on cognitive function. Alzheimers Dement. 2023;19(4):1308-1319.',
      'Blumberg JB et al. The evolving role of multivitamin/multimineral supplement use among adults. Nutrients. 2018;10(2):248.',
    ],

    gender: 'any',
    clinicalNotes: 'Foundation-level insurance policy for micronutrient gaps. Ensure methylated B-vitamins for MTHFR variants. Separate from calcium-heavy foods by 1 hour for optimal mineral absorption.',
    rationale: 'Your lab panel shows suboptimal levels across multiple micronutrients. A high-quality methylated multivitamin provides baseline coverage while we address specific deficiencies with targeted supplements.',

    pricing: {
      manufacturingCost: '$3.00-5.00',
      supplierCost: '$8-12/mo',
      monthlyPrice: '$29/mo',
    },

    status: 'active',
    sortOrder: 1,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 2. Vitamin D3 + K2
  // ─────────────────────────────────────────────
  {
    id: 'supp-vitamin-d3-k2',
    catalogNumber: 2,
    name: 'Vitamin D3 + K2',
    ingredients: 'Cholecalciferol (D3) 5000 IU, Menaquinone-7 (K2) 100 mcg',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 softgel daily (AM with fat-containing meal)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'vitamin_d_25oh',
        condition: 'below_functional',
        threshold: 40,
        description: 'Vitamin D <40 ng/mL',
      },
      {
        biomarker: 'vitamin_d_25oh',
        condition: 'below_standard',
        threshold: 30,
        description: 'Vitamin D <30 ng/mL (deficient)',
      },
    ],
    targetBiomarkers: ['vitamin_d_25oh', 'calcium', 'pth'],
    targetGoals: ['immune', 'longevity', 'hormonal'],
    pairsWith: ['supp-magnesium-glycinate', 'supp-omega-3-fish-oil', 'supp-multivitamin'],

    contraindications: ['hypercalcemia', 'granulomatous_disease_sarcoidosis', 'primary_hyperparathyroidism'],
    drugInteractions: ['thiazide_diuretics_increase_calcium', 'corticosteroids_reduce_absorption'],
    allergenFlags: ['soy_possible_in_softgel'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['25-OH_vitamin_D_at_90_days', 'serum_calcium_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Vitamin D insufficiency (<40 ng/mL) is present in over 40% of US adults. D3 supplementation at 5000 IU/day reliably raises serum levels. Co-administration of K2 (MK-7) directs calcium to bone and away from arterial walls.',
    citations: [
      'Holick MF. The vitamin D deficiency pandemic. J Clin Endocrinol Metab. 2017;102(6):1681-1690.',
      'Knapen MH et al. Three-year low-dose menaquinone-7 supplementation reduces bone loss. Osteoporos Int. 2013;24(9):2499-2507.',
    ],

    gender: 'any',
    clinicalNotes: 'Target 25-OH D3 of 50-80 ng/mL for functional optimization. K2 (MK-7 form) prevents vascular calcification. Magnesium is a required cofactor for D3 metabolism — pair with magnesium glycinate. Recheck at 90 days.',
    rationale: 'Your Vitamin D at {value} ng/mL is below the functional optimal of 50-80 ng/mL. D3 5000 IU with K2 will raise your levels while ensuring calcium is directed to bones rather than arteries.',

    pricing: {
      manufacturingCost: '$1.50-3.00',
      supplierCost: '$6-9/mo',
      monthlyPrice: '$22/mo',
    },

    status: 'active',
    sortOrder: 2,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 3. Omega-3 Fish Oil
  // ─────────────────────────────────────────────
  {
    id: 'supp-omega-3-fish-oil',
    catalogNumber: 3,
    name: 'Omega-3 Fish Oil',
    ingredients: 'EPA 2000 mg, DHA 1000 mg (triglyceride form, IFOS 5-star certified)',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '2 softgels daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'hs_crp',
        condition: 'above_functional',
        threshold: 1.0,
        description: 'hs-CRP >1.0 mg/L',
      },
      {
        biomarker: 'triglycerides',
        condition: 'above_functional',
        threshold: 100,
        description: 'Triglycerides >100 mg/dL',
      },
      {
        biomarker: 'omega3_index',
        condition: 'below_functional',
        threshold: 8,
        description: 'Omega-3 Index <8%',
      },
    ],
    targetBiomarkers: ['hs_crp', 'triglycerides', 'omega3_index', 'ldl_particle_number'],
    targetGoals: ['cardiovascular', 'cognition', 'immune'],
    pairsWith: ['supp-vitamin-d3-k2', 'supp-curcumin-turmeric', 'supp-coq10-ubiquinol'],

    contraindications: ['fish_allergy'],
    drugInteractions: ['warfarin_increased_bleeding_risk', 'antiplatelet_agents_increased_bleeding_risk'],
    allergenFlags: ['fish', 'shellfish_possible_cross_contamination'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['hs_crp_at_90_days', 'triglycerides_at_90_days', 'omega3_index_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'EPA+DHA at 2-3 g/day significantly reduces triglycerides (25-30%), lowers hs-CRP, and reduces cardiovascular mortality. The REDUCE-IT trial showed 25% relative risk reduction in cardiovascular events with high-dose EPA.',
    citations: [
      'Bhatt DL et al. Cardiovascular risk reduction with icosapent ethyl (REDUCE-IT). N Engl J Med. 2019;380(1):11-22.',
      'Calder PC. Omega-3 fatty acids and inflammatory processes. Nutrients. 2010;2(3):355-374.',
    ],

    gender: 'any',
    clinicalNotes: 'Use triglyceride-form fish oil for 70% better absorption vs ethyl ester. High-dose EPA (2g+) provides anti-inflammatory benefit beyond standard dosing. Monitor if on anticoagulants.',
    rationale: 'Your hs-CRP at {value} mg/L and triglycerides at {value} mg/dL indicate systemic inflammation and lipid imbalance. High-potency omega-3 (2g EPA + 1g DHA) will address both cardiovascular and inflammatory markers.',

    pricing: {
      manufacturingCost: '$4.00-7.00',
      supplierCost: '$12-18/mo',
      monthlyPrice: '$34/mo',
    },

    status: 'active',
    sortOrder: 3,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 4. Magnesium Glycinate
  // ─────────────────────────────────────────────
  {
    id: 'supp-magnesium-glycinate',
    catalogNumber: 4,
    name: 'Magnesium Glycinate',
    ingredients: 'Magnesium bisglycinate chelate, 400 mg elemental magnesium',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '2 caps daily (PM, 30 min before bed)',
    timing: 'PM_RECOVERY',

    triggers: [
      {
        biomarker: 'rbc_magnesium',
        condition: 'below_functional',
        threshold: 5.0,
        description: 'RBC Magnesium <5.0 mg/dL',
      },
      {
        biomarker: 'serum_magnesium',
        condition: 'below_functional',
        threshold: 2.0,
        description: 'Serum Magnesium <2.0 mg/dL',
      },
    ],
    targetBiomarkers: ['rbc_magnesium', 'serum_magnesium'],
    targetGoals: ['sleep', 'cardiovascular', 'energy'],
    pairsWith: ['supp-vitamin-d3-k2', 'supp-melatonin', 'supp-b-complex-methylated'],

    contraindications: ['severe_renal_insufficiency_gfr_below_30', 'myasthenia_gravis'],
    drugInteractions: ['bisphosphonates_separate_by_2hrs', 'antibiotics_fluoroquinolones_separate_by_4hrs', 'diuretics_may_increase_magnesium_loss'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['rbc_magnesium_at_90_days', 'serum_magnesium_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Up to 50% of Americans are magnesium deficient. Glycinate form is the best-tolerated (least GI distress) and has calming properties via glycine receptor activation. 400 mg elemental reliably corrects deficiency in 8-12 weeks.',
    citations: [
      'Costello RB et al. Perspective: the case for an evidence-based reference interval for serum magnesium. Adv Nutr. 2016;7(6):977-993.',
      'Abbasi B et al. The effect of magnesium supplementation on primary insomnia in elderly. J Res Med Sci. 2012;17(12):1161-1169.',
    ],

    gender: 'any',
    clinicalNotes: 'Glycinate form preferred for sleep and neurological benefits. Serum Mg is a poor marker (only 1% of body Mg is extracellular) — use RBC Magnesium for accurate status. Required cofactor for Vitamin D metabolism. PM dosing supports GABA activity and sleep architecture.',
    rationale: 'Your RBC Magnesium at {value} mg/dL is below the functional threshold of 5.0 mg/dL. Magnesium glycinate at 400 mg elemental will restore levels while supporting sleep quality and vitamin D metabolism.',

    pricing: {
      manufacturingCost: '$2.50-4.00',
      supplierCost: '$8-12/mo',
      monthlyPrice: '$26/mo',
    },

    status: 'active',
    sortOrder: 4,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 5. Probiotic
  // ─────────────────────────────────────────────
  {
    id: 'supp-probiotic',
    catalogNumber: 5,
    name: 'Probiotic',
    ingredients: 'Multi-strain blend: L. acidophilus, L. rhamnosus GG, B. longum, B. lactis, S. boulardii — 50 billion CFU',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM on empty stomach or with light meal)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'gut_health',
        condition: 'any_suboptimal',
        description: 'GI symptoms, antibiotic history, or systemic inflammation',
      },
      {
        biomarker: 'hs_crp',
        condition: 'above_functional',
        threshold: 1.0,
        description: 'hs-CRP >1.0 mg/L (gut-mediated inflammation)',
      },
    ],
    targetBiomarkers: ['hs_crp', 'gut_health'],
    targetGoals: ['immune', 'energy', 'body_composition'],
    pairsWith: ['supp-fiber-supplement', 'supp-digestive-enzymes', 'supp-omega-3-fish-oil'],

    contraindications: ['immunocompromised_severe', 'short_bowel_syndrome', 'central_venous_catheter_risk_of_fungemia_with_s_boulardii'],
    drugInteractions: ['immunosuppressants_use_with_caution', 'antibiotics_separate_by_2hrs'],
    allergenFlags: ['dairy_trace_possible', 'soy_trace_possible'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['symptom_assessment_at_30_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Multi-strain probiotics at 50B+ CFU improve gut barrier integrity, reduce systemic inflammation (hs-CRP), and support immune function. L. rhamnosus GG is the most clinically studied strain with strong evidence for immune modulation.',
    citations: [
      'Hao Q et al. Probiotics for preventing acute upper respiratory tract infections. Cochrane Database Syst Rev. 2015;(2):CD006895.',
      'Zhang C et al. Ecological robustness of the gut microbiota in response to ingestion of transient food-borne microbes. ISME J. 2016;10(9):2235-2245.',
    ],

    gender: 'any',
    clinicalNotes: 'Multi-strain preferred over single-strain for broad-spectrum gut support. Refrigerated strains have better viability. S. boulardii provides yeast-based balance distinct from bacterial strains. If SIBO is suspected, delay probiotics until cleared.',
    rationale: 'Gut health optimization is foundational to nutrient absorption and immune function. A high-potency multi-strain probiotic supports your microbiome diversity and can help reduce the systemic inflammation reflected in your inflammatory markers.',

    pricing: {
      manufacturingCost: '$4.00-7.00',
      supplierCost: '$10-16/mo',
      monthlyPrice: '$32/mo',
    },

    status: 'active',
    sortOrder: 5,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 6. B-Complex (Methylated)
  // ─────────────────────────────────────────────
  {
    id: 'supp-b-complex-methylated',
    catalogNumber: 6,
    name: 'B-Complex (Methylated)',
    ingredients: 'L-5-MTHF (methylfolate) 800 mcg, methylcobalamin (B12) 1000 mcg, P-5-P (B6) 25 mg, riboflavin-5-phosphate (B2) 25 mg, benfotiamine (B1) 50 mg, niacinamide (B3) 50 mg, pantothenic acid (B5) 50 mg, biotin 300 mcg',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'vitamin_b12',
        condition: 'below_functional',
        threshold: 500,
        description: 'Vitamin B12 <500 pg/mL',
      },
      {
        biomarker: 'folate',
        condition: 'below_functional',
        threshold: 15,
        description: 'Folate <15 ng/mL',
      },
      {
        biomarker: 'homocysteine',
        condition: 'above_functional',
        threshold: 8,
        description: 'Homocysteine >8 umol/L',
      },
    ],
    targetBiomarkers: ['vitamin_b12', 'folate', 'homocysteine', 'mma'],
    targetGoals: ['energy', 'cognition', 'cardiovascular'],
    pairsWith: ['supp-multivitamin', 'supp-iron-bisglycinate', 'supp-magnesium-glycinate'],

    contraindications: ['cobalt_allergy'],
    drugInteractions: ['methotrexate_folate_interaction_consult_oncologist', 'phenytoin_may_reduce_folate_levels', 'proton_pump_inhibitors_reduce_b12_absorption'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['vitamin_b12_at_90_days', 'homocysteine_at_90_days', 'folate_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Methylated B-vitamins bypass MTHFR polymorphisms (present in up to 40% of the population) ensuring bioavailability. B12 + folate directly lower homocysteine, an independent cardiovascular risk factor. P-5-P form of B6 is the active coenzyme form.',
    citations: [
      'Stover PJ. Polymorphisms in 1-carbon metabolism, epigenetics and folate-related pathologies. J Nutrigenet Nutrigenomics. 2011;4(5):293-305.',
      'Clarke R et al. Effects of lowering homocysteine levels with B vitamins on cardiovascular disease. Arch Intern Med. 2010;170(18):1622-1631.',
    ],

    gender: 'any',
    clinicalNotes: 'Methylated forms critical for ~40% of patients with MTHFR C677T or A1298C variants. If B12 is <500 pg/mL, consider sublingual methylcobalamin for 60 days before switching to oral maintenance. Monitor homocysteine as the functional marker of B-vitamin adequacy.',
    rationale: 'Your B12 at {value} pg/mL is below the functional optimal of 500-1000 pg/mL. A methylated B-complex ensures your body can utilize these critical vitamins regardless of MTHFR status, supporting energy production and reducing cardiovascular risk via homocysteine lowering.',

    pricing: {
      manufacturingCost: '$3.00-5.00',
      supplierCost: '$9-14/mo',
      monthlyPrice: '$28/mo',
    },

    status: 'active',
    sortOrder: 6,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 7. Iron Bisglycinate
  // ─────────────────────────────────────────────
  {
    id: 'supp-iron-bisglycinate',
    catalogNumber: 7,
    name: 'Iron Bisglycinate',
    ingredients: 'Ferrous bisglycinate chelate, 25 mg elemental iron',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (midday, 2 hrs away from calcium/coffee/tea)',
    timing: 'MIDDAY_ABSORPTION',

    triggers: [
      {
        biomarker: 'ferritin',
        condition: 'below_functional',
        threshold: 30,
        description: 'Ferritin <30 ng/mL',
      },
      {
        biomarker: 'iron_saturation',
        condition: 'below_functional',
        threshold: 20,
        description: 'Iron saturation <20%',
      },
      {
        biomarker: 'hemoglobin',
        condition: 'below_standard',
        threshold: 12,
        description: 'Hemoglobin <12 g/dL (female) or <13.5 g/dL (male)',
      },
    ],
    targetBiomarkers: ['ferritin', 'iron_saturation', 'hemoglobin', 'tibc'],
    targetGoals: ['energy', 'hormonal'],
    pairsWith: ['supp-vitamin-c', 'supp-b-complex-methylated'],

    contraindications: ['hemochromatosis', 'iron_overload', 'hemosiderosis', 'repeated_blood_transfusions', 'thalassemia_major'],
    drugInteractions: ['levothyroxine_separate_by_4hrs', 'tetracycline_separate_by_2hrs', 'antacids_reduce_absorption', 'calcium_supplements_separate_by_2hrs'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['ferritin_at_90_days', 'iron_panel_at_90_days', 'cbc_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Iron bisglycinate chelate has 3-4x better absorption than ferrous sulfate with significantly less GI side effects. 25 mg every-other-day dosing may be equally effective as daily dosing due to hepcidin kinetics (Moretti et al. 2015).',
    citations: [
      'Moretti D et al. Oral iron supplements increase hepcidin and decrease iron absorption from daily or twice-daily doses. Blood. 2015;126(17):1981-1989.',
      'Name JJ et al. Iron bisglycinate chelate and polymaltose iron for treatment of iron deficiency anemia. Curr Med Res Opin. 2018;34(6):1103-1108.',
    ],

    gender: 'any',
    clinicalNotes: 'Female priority — menstruating women are at highest risk for iron deficiency. ALWAYS check ferritin before starting iron. Bisglycinate form is critical for tolerability. Take with vitamin C to enhance absorption. Separate from calcium, coffee, and tea by 2 hours. Contraindicated in hemochromatosis — ferritin >300 in males or >200 in females warrants iron studies before supplementation.',
    rationale: 'Your Ferritin at {value} ng/mL is below the functional threshold of 30 ng/mL, indicating depleted iron stores. Iron bisglycinate (25 mg) is the best-tolerated form and will restore your ferritin over 90 days. Take with vitamin C for enhanced absorption.',

    pricing: {
      manufacturingCost: '$1.50-3.00',
      supplierCost: '$5-8/mo',
      monthlyPrice: '$18/mo',
    },

    status: 'active',
    sortOrder: 7,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 8. CoQ10 (Ubiquinol)
  // ─────────────────────────────────────────────
  {
    id: 'supp-coq10-ubiquinol',
    catalogNumber: 8,
    name: 'CoQ10 (Ubiquinol)',
    ingredients: 'Ubiquinol (reduced CoQ10) 200 mg, in lipid-soluble base',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 softgel daily (AM with fat-containing meal)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'coq10',
        condition: 'below_functional',
        threshold: 1.0,
        description: 'CoQ10 <1.0 mcg/mL',
      },
      {
        biomarker: 'statin_use',
        condition: 'any_suboptimal',
        description: 'Currently on statin therapy (depletes CoQ10)',
      },
    ],
    targetBiomarkers: ['coq10', 'energy_markers'],
    targetGoals: ['energy', 'cardiovascular', 'longevity'],
    pairsWith: ['supp-omega-3-fish-oil', 'supp-alpha-lipoic-acid', 'supp-magnesium-glycinate'],

    contraindications: [],
    drugInteractions: ['statins_deplete_coq10_supplementation_recommended', 'warfarin_may_reduce_inr_monitor_closely', 'beta_blockers_may_be_depleted_by_some'],
    allergenFlags: ['soy_possible_in_softgel'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['coq10_levels_at_90_days_if_available'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Ubiquinol is the reduced, bioactive form of CoQ10 with 3-6x better absorption than ubiquinone. Essential for mitochondrial ATP production. Statins inhibit the mevalonate pathway, depleting CoQ10 by 40%. The Q-SYMBIO trial showed reduced cardiovascular mortality with CoQ10 supplementation.',
    citations: [
      'Mortensen SA et al. The effect of coenzyme Q10 on morbidity and mortality in chronic heart failure (Q-SYMBIO). JACC Heart Fail. 2014;2(6):641-649.',
      'Langsjoen PH, Langsjoen AM. Comparison study of plasma coenzyme Q10 levels in healthy subjects supplemented with ubiquinol versus ubiquinone. Clin Pharmacol Drug Dev. 2014;3(1):13-17.',
    ],

    gender: 'any',
    clinicalNotes: 'Ubiquinol form is critical — ubiquinone conversion declines with age. Mandatory co-prescription for all statin users. 200 mg is the clinically effective dose for cardiovascular support. Take with fat for optimal absorption. Over age 40, endogenous production declines significantly.',
    rationale: 'Your CoQ10 levels and/or statin use indicate a need for ubiquinol supplementation. At 200 mg daily, this supports mitochondrial energy production and cardiovascular function, especially important given that statin therapy depletes CoQ10 by up to 40%.',

    pricing: {
      manufacturingCost: '$5.00-9.00',
      supplierCost: '$14-20/mo',
      monthlyPrice: '$38/mo',
    },

    status: 'active',
    sortOrder: 8,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 9. Berberine
  // ─────────────────────────────────────────────
  {
    id: 'supp-berberine',
    catalogNumber: 9,
    name: 'Berberine',
    ingredients: 'Berberine HCl 500 mg (from Berberis aristata bark)',
    category: 'supplement',
    subcategory: 'weight_metabolic',
    tier: 1,
    route: 'oral',
    dosage: '1 cap twice daily (midday + PM, with meals)',
    timing: 'MIDDAY_ABSORPTION',
    additionalTimings: ['PM_RECOVERY'],

    triggers: [
      {
        biomarker: 'fasting_insulin',
        condition: 'above_functional',
        threshold: 7,
        description: 'Fasting insulin >7 uIU/mL',
      },
      {
        biomarker: 'fasting_glucose',
        condition: 'above_functional',
        threshold: 95,
        description: 'Fasting glucose >95 mg/dL',
      },
      {
        biomarker: 'hba1c',
        condition: 'above_functional',
        threshold: 5.5,
        description: 'HbA1c >5.5%',
      },
      {
        biomarker: 'homa_ir',
        condition: 'above_functional',
        threshold: 1.5,
        description: 'HOMA-IR >1.5',
      },
    ],
    targetBiomarkers: ['fasting_insulin', 'fasting_glucose', 'hba1c', 'homa_ir', 'triglycerides', 'ldl_cholesterol'],
    targetGoals: ['body_composition', 'cardiovascular', 'longevity'],
    pairsWith: ['supp-chromium-picolinate', 'supp-alpha-lipoic-acid', 'supp-fiber-supplement'],

    contraindications: ['pregnancy', 'breastfeeding', 'hypoglycemia_history'],
    drugInteractions: ['metformin_additive_hypoglycemia_risk_do_not_combine_without_md', 'cyclosporine_increases_levels', 'cyp3a4_substrates_berberine_inhibits_cyp3a4', 'sulfonylureas_increased_hypoglycemia_risk'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['fasting_insulin_at_90_days', 'fasting_glucose_at_90_days', 'hba1c_at_90_days', 'lipid_panel_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Berberine activates AMPK, producing metabolic effects comparable to metformin. Meta-analyses show berberine reduces fasting glucose by 15-25 mg/dL, HbA1c by 0.5-0.9%, and triglycerides by 35-50 mg/dL. Effective for insulin resistance and metabolic syndrome.',
    citations: [
      'Liang Y et al. Effects of berberine on blood glucose in patients with type 2 diabetes: a systematic review and meta-analysis. Endocr Metab Immune Disord Drug Targets. 2019;19(4):563-573.',
      'Yin J et al. Efficacy of berberine in patients with type 2 diabetes mellitus. Metabolism. 2008;57(5):712-717.',
    ],

    gender: 'any',
    clinicalNotes: 'CRITICAL: Do not combine with metformin without physician oversight — additive hypoglycemia risk. Split dosing (500 mg BID) is better tolerated than 1000 mg single dose. Activates AMPK pathway similar to metformin. Monitor for GI side effects in first 2 weeks. Potent CYP3A4 inhibitor — check for drug interactions with patient medication list.',
    rationale: 'Your fasting insulin at {value} uIU/mL and glucose at {value} mg/dL indicate insulin resistance. Berberine 500 mg twice daily activates AMPK (the same pathway as metformin), shown to reduce glucose by 15-25 mg/dL and improve insulin sensitivity.',

    pricing: {
      manufacturingCost: '$2.00-4.00',
      supplierCost: '$8-12/mo',
      monthlyPrice: '$26/mo',
    },

    status: 'active',
    sortOrder: 9,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 10. Green Tea Extract (EGCG)
  // ─────────────────────────────────────────────
  {
    id: 'supp-green-tea-extract-egcg',
    catalogNumber: 10,
    name: 'Green Tea Extract (EGCG)',
    ingredients: 'Green tea leaf extract standardized to 50% EGCG, 500 mg total (250 mg EGCG)',
    category: 'supplement',
    subcategory: 'weight_metabolic',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'body_composition',
        condition: 'any_suboptimal',
        description: 'Elevated body fat percentage or BMI >25',
      },
      {
        biomarker: 'fasting_glucose',
        condition: 'above_functional',
        threshold: 95,
        description: 'Fasting glucose >95 mg/dL',
      },
    ],
    targetBiomarkers: ['body_composition', 'fasting_glucose', 'ldl_cholesterol', 'metabolic_rate'],
    targetGoals: ['body_composition', 'longevity', 'cognition'],
    pairsWith: ['supp-berberine', 'supp-chromium-picolinate', 'supp-omega-3-fish-oil'],

    contraindications: ['liver_disease_hepatotoxicity_at_high_doses', 'iron_deficiency_anemia_reduces_iron_absorption'],
    drugInteractions: ['nadolol_reduced_absorption', 'warfarin_may_reduce_inr', 'stimulant_medications_additive_caffeine_effect', 'iron_supplements_separate_by_2hrs'],
    allergenFlags: ['caffeine_30_50mg_per_cap'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['liver_enzymes_at_90_days', 'body_composition_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'EGCG increases thermogenesis and fat oxidation by 10-16% via catechol-O-methyltransferase (COMT) inhibition and norepinephrine prolongation. Also shows antioxidant, neuroprotective, and glucose-lowering effects. Keep doses under 800 mg EGCG/day to avoid hepatotoxicity.',
    citations: [
      'Hursel R et al. The effects of green tea on weight loss and weight maintenance: a meta-analysis. Int J Obes. 2009;33(9):956-961.',
      'Mancini E et al. Green tea effects on cognition, mood and human brain function. Phytomedicine. 2017;34:26-37.',
    ],

    gender: 'any',
    clinicalNotes: 'Use with food to reduce hepatotoxicity risk. EGCG content should not exceed 800 mg/day. Contains caffeine (~30-50 mg/cap) — consider in caffeine-sensitive patients. Inhibits iron absorption — separate from iron supplements by 2 hours. Monitor liver enzymes (ALT/AST) at 90 days.',
    rationale: 'Your metabolic markers indicate suboptimal body composition and glucose handling. Green tea extract (500 mg standardized to EGCG) supports thermogenesis and fat oxidation while providing antioxidant and neuroprotective benefits.',

    pricing: {
      manufacturingCost: '$1.50-3.00',
      supplierCost: '$6-10/mo',
      monthlyPrice: '$22/mo',
    },

    status: 'active',
    sortOrder: 10,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 11. Fiber Supplement
  // ─────────────────────────────────────────────
  {
    id: 'supp-fiber-supplement',
    catalogNumber: 11,
    name: 'Fiber Supplement',
    ingredients: 'Organic psyllium husk powder, 5 g soluble fiber per serving',
    category: 'supplement',
    subcategory: 'weight_metabolic',
    tier: 1,
    route: 'oral',
    dosage: '1 scoop (5g) in water daily (PM, 30 min before dinner)',
    timing: 'PM_RECOVERY',

    triggers: [
      {
        biomarker: 'fasting_glucose',
        condition: 'above_functional',
        threshold: 95,
        description: 'Fasting glucose >95 mg/dL',
      },
      {
        biomarker: 'ldl_cholesterol',
        condition: 'above_functional',
        threshold: 100,
        description: 'LDL cholesterol >100 mg/dL',
      },
      {
        biomarker: 'triglycerides',
        condition: 'above_functional',
        threshold: 100,
        description: 'Triglycerides >100 mg/dL',
      },
    ],
    targetBiomarkers: ['fasting_glucose', 'ldl_cholesterol', 'triglycerides', 'hba1c'],
    targetGoals: ['body_composition', 'cardiovascular'],
    pairsWith: ['supp-probiotic', 'supp-berberine', 'supp-digestive-enzymes'],

    contraindications: ['bowel_obstruction', 'difficulty_swallowing_esophageal_stricture', 'fecal_impaction'],
    drugInteractions: ['all_medications_separate_by_2hrs_reduces_absorption', 'levothyroxine_separate_by_4hrs', 'lithium_may_reduce_absorption'],
    allergenFlags: ['psyllium_allergy_rare'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['lipid_panel_at_90_days', 'fasting_glucose_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Psyllium husk 5-10 g/day lowers LDL cholesterol by 5-10%, reduces post-prandial glucose spikes by 20%, and improves bowel regularity. FDA-approved heart health claim. Pre-meal dosing blunts glycemic response of subsequent meals.',
    citations: [
      'Wei ZH et al. Time- and dose-dependent effect of psyllium on serum lipids. Eur J Clin Nutr. 2009;63(7):821-827.',
      'Gibb RD et al. Psyllium fiber improves glycemic control. Nutr Rev. 2015;73(12):822-828.',
    ],

    gender: 'any',
    clinicalNotes: 'Must take with full glass of water (240 mL+) to prevent choking/obstruction. Separate from ALL medications by at least 2 hours as fiber can bind drugs. Start at 2.5g and increase to 5g over 1 week to minimize bloating. Pre-dinner timing blunts post-prandial glucose.',
    rationale: 'Your lipid and glucose markers show room for improvement. Psyllium fiber (5g) taken before dinner reduces LDL by 5-10%, blunts post-meal glucose spikes, and supports healthy elimination — a simple intervention with strong cardiovascular evidence.',

    pricing: {
      manufacturingCost: '$1.00-2.00',
      supplierCost: '$4-7/mo',
      monthlyPrice: '$16/mo',
    },

    status: 'active',
    sortOrder: 11,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 12. Zinc + Copper
  // ─────────────────────────────────────────────
  {
    id: 'supp-zinc-copper',
    catalogNumber: 12,
    name: 'Zinc + Copper',
    ingredients: 'Zinc picolinate 30 mg, copper bisglycinate 2 mg',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'zinc',
        condition: 'below_functional',
        threshold: 80,
        description: 'Serum zinc <80 mcg/dL',
      },
      {
        biomarker: 'testosterone_total',
        condition: 'below_functional',
        threshold: 500,
        description: 'Total testosterone <500 ng/dL (male)',
      },
    ],
    targetBiomarkers: ['zinc', 'copper', 'testosterone_total', 'testosterone_free'],
    targetGoals: ['immune', 'hormonal', 'sexual_health'],
    pairsWith: ['supp-magnesium-glycinate', 'supp-vitamin-d3-k2', 'supp-tongkat-ali'],

    contraindications: ['wilsons_disease_copper_component'],
    drugInteractions: ['penicillamine_separate_by_2hrs', 'tetracycline_separate_by_2hrs', 'quinolone_antibiotics_separate_by_2hrs'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['serum_zinc_at_90_days', 'serum_copper_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Zinc at 30 mg/day supports testosterone synthesis, immune function, and over 300 enzymatic reactions. Copper co-supplementation at a 15:1 ratio (Zn:Cu) prevents zinc-induced copper deficiency, which can cause anemia and neutropenia.',
    citations: [
      'Prasad AS. Zinc is an antioxidant and anti-inflammatory agent: its role in human health. Front Nutr. 2014;1:14.',
      'Willis MS et al. Zinc-induced copper deficiency: a report of three cases initially recognized on bone marrow examination. Am J Clin Pathol. 2005;123(1):125-131.',
    ],

    gender: 'any',
    clinicalNotes: 'ALWAYS pair zinc with copper to prevent depletion. Zinc >30 mg/day without copper causes copper deficiency anemia within 6-12 months. 15:1 zinc-to-copper ratio is the physiologic standard. Zinc picolinate has superior absorption. Supports 5-alpha reductase activity and testosterone conversion.',
    rationale: 'Your zinc levels and/or testosterone markers indicate a need for zinc support. The 30 mg zinc + 2 mg copper formulation maintains the critical 15:1 ratio, supporting immune function and hormonal health while preventing copper depletion.',

    pricing: {
      manufacturingCost: '$1.00-2.00',
      supplierCost: '$4-7/mo',
      monthlyPrice: '$16/mo',
    },

    status: 'active',
    sortOrder: 12,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 13. Selenium
  // ─────────────────────────────────────────────
  {
    id: 'supp-selenium',
    catalogNumber: 13,
    name: 'Selenium',
    ingredients: 'Selenomethionine 200 mcg',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'selenium',
        condition: 'below_functional',
        threshold: 120,
        description: 'Serum selenium <120 mcg/L',
      },
      {
        biomarker: 'free_t3',
        condition: 'below_functional',
        threshold: 3.0,
        description: 'Free T3 <3.0 pg/mL (impaired T4→T3 conversion)',
      },
      {
        biomarker: 'tpo_antibodies',
        condition: 'above_standard',
        threshold: 34,
        description: 'TPO antibodies >34 IU/mL (Hashimoto\'s)',
      },
    ],
    targetBiomarkers: ['selenium', 'free_t3', 'free_t4', 'tpo_antibodies', 'thyroglobulin_antibodies'],
    targetGoals: ['hormonal', 'immune', 'longevity'],
    pairsWith: ['supp-zinc-copper', 'supp-vitamin-d3-k2'],

    contraindications: ['selenium_toxicity_selenosis'],
    drugInteractions: ['cisplatin_may_reduce_selenium_levels', 'statin_drugs_selenium_may_affect_cholesterol'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['selenium_at_90_days', 'thyroid_panel_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Selenium is a required cofactor for deiodinase enzymes (T4→T3 conversion) and glutathione peroxidase. 200 mcg selenomethionine reduces TPO antibodies by 21-40% in Hashimoto\'s thyroiditis. Do not exceed 400 mcg/day total from all sources.',
    citations: [
      'Rayman MP. Selenium and human health. Lancet. 2012;379(9822):1256-1268.',
      'Wichman J et al. Selenium supplementation significantly reduces thyroid autoantibodies: a systematic review and meta-analysis. Thyroid. 2016;26(12):1681-1692.',
    ],

    gender: 'any',
    clinicalNotes: 'Selenomethionine form preferred for long-term use (incorporated into selenoproteins). Critical for T4→T3 conversion via deiodinase enzymes. In Hashimoto\'s, 200 mcg/day reduces TPO antibodies significantly. Do not exceed 400 mcg/day total (food + supplement). Signs of selenosis: garlic breath, brittle nails, hair loss.',
    rationale: 'Your thyroid conversion markers suggest suboptimal T4→T3 activity. Selenium at 200 mcg (as selenomethionine) is the required cofactor for deiodinase enzymes and also supports glutathione production for thyroid autoimmune protection.',

    pricing: {
      manufacturingCost: '$0.75-1.50',
      supplierCost: '$3-6/mo',
      monthlyPrice: '$14/mo',
    },

    status: 'active',
    sortOrder: 13,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 14. Vitamin C
  // ─────────────────────────────────────────────
  {
    id: 'supp-vitamin-c',
    catalogNumber: 14,
    name: 'Vitamin C',
    ingredients: 'Ascorbic acid 500 mg + citrus bioflavonoids 500 mg (buffered)',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 cap twice daily (AM + midday, 1000 mg total)',
    timing: 'AM_ENERGY',
    additionalTimings: ['MIDDAY_ABSORPTION'],

    triggers: [
      {
        biomarker: 'vitamin_c',
        condition: 'below_functional',
        threshold: 1.0,
        description: 'Plasma vitamin C <1.0 mg/dL',
      },
      {
        biomarker: 'hs_crp',
        condition: 'above_functional',
        threshold: 1.0,
        description: 'hs-CRP >1.0 mg/L (antioxidant support)',
      },
    ],
    targetBiomarkers: ['vitamin_c', 'hs_crp', 'iron_absorption'],
    targetGoals: ['immune', 'longevity', 'energy'],
    pairsWith: ['supp-iron-bisglycinate', 'supp-nac', 'supp-vitamin-e-mixed-tocopherols'],

    contraindications: ['oxalate_kidney_stones_high_doses', 'g6pd_deficiency_high_doses', 'hemochromatosis_enhances_iron_absorption'],
    drugInteractions: ['warfarin_high_doses_may_reduce_inr', 'chemotherapy_agents_antioxidant_interaction_consult_oncologist', 'aluminum_containing_antacids_increased_aluminum_absorption'],
    allergenFlags: ['citrus_derived'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['plasma_vitamin_c_at_90_days_if_available'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Vitamin C at 1000 mg/day supports collagen synthesis, immune function (reduces cold duration by 8%), and enhances non-heme iron absorption by 2-3x. Acts as primary water-soluble antioxidant regenerating vitamin E. Buffered form reduces GI irritation.',
    citations: [
      'Hemila H, Chalker E. Vitamin C for preventing and treating the common cold. Cochrane Database Syst Rev. 2013;(1):CD000980.',
      'Carr AC, Maggini S. Vitamin C and immune function. Nutrients. 2017;9(11):1211.',
    ],

    gender: 'any',
    clinicalNotes: 'Split dosing (500 mg BID) maximizes absorption — single doses >500 mg have diminishing bioavailability. Enhances non-heme iron absorption 2-3x (pair with iron supplement). Caution in oxalate stone formers at doses >2000 mg/day. Buffered form reduces GI upset. False-negatively lowers some glucose meter readings.',
    rationale: 'Vitamin C at 1000 mg daily (split dosing) provides foundational antioxidant protection, enhances your iron absorption, and supports immune function. The buffered form with bioflavonoids ensures gentle, sustained delivery.',

    pricing: {
      manufacturingCost: '$1.00-2.00',
      supplierCost: '$4-7/mo',
      monthlyPrice: '$16/mo',
    },

    status: 'active',
    sortOrder: 14,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 15. NAC (N-Acetyl Cysteine)
  // ─────────────────────────────────────────────
  {
    id: 'supp-nac',
    catalogNumber: 15,
    name: 'NAC (N-Acetyl Cysteine)',
    ingredients: 'N-Acetyl-L-Cysteine 600 mg',
    category: 'supplement',
    subcategory: 'antioxidant_detox',
    tier: 1,
    route: 'oral',
    dosage: '1 cap twice daily (AM + PM, 1200 mg total)',
    timing: 'AM_ENERGY',
    additionalTimings: ['PM_RECOVERY'],

    triggers: [
      {
        biomarker: 'hs_crp',
        condition: 'above_functional',
        threshold: 1.0,
        description: 'hs-CRP >1.0 mg/L',
      },
      {
        biomarker: 'ggt',
        condition: 'above_functional',
        threshold: 30,
        description: 'GGT >30 U/L (oxidative stress / liver burden)',
      },
      {
        biomarker: 'alt',
        condition: 'above_functional',
        threshold: 25,
        description: 'ALT >25 U/L (hepatic stress)',
      },
    ],
    targetBiomarkers: ['hs_crp', 'ggt', 'alt', 'ast', 'glutathione'],
    targetGoals: ['longevity', 'immune', 'cardiovascular'],
    pairsWith: ['supp-vitamin-c', 'supp-selenium', 'supp-alpha-lipoic-acid'],

    contraindications: ['active_peptic_ulcer_may_increase_gastric_acidity'],
    drugInteractions: ['nitroglycerin_potentiates_vasodilation_and_headache', 'activated_charcoal_reduced_absorption', 'carbamazepine_may_increase_toxicity'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['hs_crp_at_90_days', 'liver_enzymes_at_90_days', 'ggt_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'NAC is the rate-limiting precursor to glutathione, the body\'s master antioxidant. 1200 mg/day replenishes intracellular glutathione by 30-40%, reduces hs-CRP, supports hepatic detoxification phase II, and has mucolytic properties. Hospital-grade antidote for acetaminophen toxicity.',
    citations: [
      'Atkuri KR et al. N-Acetylcysteine — a safe antidote for cysteine/glutathione deficiency. Curr Opin Pharmacol. 2007;7(4):355-359.',
      'De Flora S et al. Attenuation of influenza-like symptomatology and improvement of cell-mediated immunity with NAC. Eur Respir J. 1997;10(7):1535-1541.',
    ],

    gender: 'any',
    clinicalNotes: 'Primary glutathione precursor — glutathione itself has poor oral bioavailability. 600 mg BID is the standard clinical dose. Supports liver phase II detoxification (glutathione conjugation). GGT is a sensitive marker of glutathione depletion. May have mild blood-thinning effect. Take on empty stomach for best absorption or with food if GI sensitive.',
    rationale: 'Your liver enzymes and/or inflammatory markers suggest increased oxidative burden. NAC at 1200 mg/day is the most effective way to raise glutathione (the body\'s master antioxidant), supporting liver detoxification and reducing systemic inflammation.',

    pricing: {
      manufacturingCost: '$2.00-3.50',
      supplierCost: '$7-11/mo',
      monthlyPrice: '$24/mo',
    },

    status: 'active',
    sortOrder: 15,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 16. Curcumin (Turmeric)
  // ─────────────────────────────────────────────
  {
    id: 'supp-curcumin-turmeric',
    catalogNumber: 16,
    name: 'Curcumin (Turmeric)',
    ingredients: 'Curcumin extract 500 mg (standardized to 95% curcuminoids) + piperine (BioPerine) 5 mg',
    category: 'supplement',
    subcategory: 'immune_inflammation',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with fat-containing meal)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'hs_crp',
        condition: 'above_functional',
        threshold: 1.0,
        description: 'hs-CRP >1.0 mg/L',
      },
      {
        biomarker: 'esr',
        condition: 'above_functional',
        threshold: 10,
        description: 'ESR >10 mm/hr',
      },
    ],
    targetBiomarkers: ['hs_crp', 'esr', 'il6', 'tnf_alpha'],
    targetGoals: ['immune', 'longevity', 'cardiovascular'],
    pairsWith: ['supp-omega-3-fish-oil', 'supp-nac', 'supp-vitamin-d3-k2'],

    contraindications: ['gallbladder_disease_gallstones', 'bile_duct_obstruction'],
    drugInteractions: ['blood_thinners_warfarin_increased_bleeding_risk', 'sulfasalazine_curcumin_may_increase_levels', 'cyclosporine_curcumin_may_increase_levels', 'piperine_increases_bioavailability_of_many_drugs_check_med_list'],
    allergenFlags: ['black_pepper_piperine'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['hs_crp_at_90_days', 'esr_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Curcumin inhibits NF-kB, COX-2, and LOX pathways. Meta-analyses demonstrate significant reductions in hs-CRP (mean -1.55 mg/L), IL-6, and TNF-alpha. Piperine increases curcumin bioavailability by 2000%. Anti-inflammatory potency comparable to ibuprofen in some studies.',
    citations: [
      'Sahebkar A et al. Effect of curcuminoids on oxidative stress: a systematic review and meta-analysis. Pharmacol Res. 2015;95-96:12-21.',
      'Chandran B, Goel A. A randomized pilot study to assess the efficacy and safety of curcumin in patients with active rheumatoid arthritis. Phytother Res. 2012;26(11):1719-1725.',
    ],

    gender: 'any',
    clinicalNotes: 'MUST include piperine (BioPerine) for adequate bioavailability — curcumin alone has <1% absorption. Take with fat-containing meal. Caution with gallbladder disease (stimulates bile production). Piperine also increases bioavailability of other medications — review full med list for interactions. Anti-inflammatory without GI side effects of NSAIDs.',
    rationale: 'Your hs-CRP at {value} mg/L indicates systemic inflammation. Curcumin with piperine is a potent NF-kB inhibitor shown to lower hs-CRP by an average of 1.55 mg/L, providing anti-inflammatory support without the GI risks of chronic NSAID use.',

    pricing: {
      manufacturingCost: '$2.50-4.50',
      supplierCost: '$8-13/mo',
      monthlyPrice: '$28/mo',
    },

    status: 'active',
    sortOrder: 16,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 17. Ashwagandha (KSM-66)
  // ─────────────────────────────────────────────
  {
    id: 'supp-ashwagandha-ksm66',
    catalogNumber: 17,
    name: 'Ashwagandha (KSM-66)',
    ingredients: 'KSM-66 Ashwagandha root extract (Withania somnifera) 600 mg, standardized to 5% withanolides',
    category: 'supplement',
    subcategory: 'hormone_precursor',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (PM with dinner)',
    timing: 'PM_RECOVERY',

    triggers: [
      {
        biomarker: 'cortisol_am',
        condition: 'above_functional',
        threshold: 18,
        description: 'Morning cortisol >18 mcg/dL (elevated stress response)',
      },
      {
        biomarker: 'dhea_s',
        condition: 'below_functional',
        threshold: 250,
        description: 'DHEA-S <250 mcg/dL',
      },
      {
        biomarker: 'testosterone_total',
        condition: 'below_functional',
        threshold: 500,
        description: 'Total testosterone <500 ng/dL (male)',
      },
    ],
    targetBiomarkers: ['cortisol_am', 'dhea_s', 'testosterone_total', 'testosterone_free'],
    targetGoals: ['hormonal', 'sleep', 'sexual_health', 'energy'],
    pairsWith: ['supp-magnesium-glycinate', 'supp-tongkat-ali', 'supp-zinc-copper'],

    contraindications: ['hyperthyroidism_may_increase_thyroid_hormones', 'autoimmune_conditions_immunostimulatory', 'pregnancy', 'nightshade_allergy_solanaceae_family'],
    drugInteractions: ['thyroid_medications_may_increase_thyroid_hormone_levels', 'sedatives_benzodiazepines_additive_sedation', 'immunosuppressants_may_counteract_effect'],
    allergenFlags: ['nightshade_family_solanaceae'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['cortisol_at_90_days', 'testosterone_panel_at_90_days', 'thyroid_panel_at_90_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'KSM-66 ashwagandha reduces cortisol by 23-30% and increases testosterone by 14-17% in stressed adults. Also improves VO2 max, sleep quality (SOL and WASO), and reduces anxiety (Hamilton Anxiety Scale). Adaptogenic effects via HPA axis modulation.',
    citations: [
      'Lopresti AL et al. An investigation into the stress-relieving and pharmacological actions of an ashwagandha extract. Medicine (Baltimore). 2019;98(37):e17186.',
      'Wankhede S et al. Examining the effect of Withania somnifera on muscle strength and recovery. J Int Soc Sports Nutr. 2015;12:43.',
    ],

    gender: 'any',
    clinicalNotes: 'KSM-66 is the most clinically studied extract — do not substitute with other forms. PM dosing leverages cortisol-lowering effect for sleep. May increase thyroid hormones — monitor in hypothyroid patients on levothyroxine. Avoid in hyperthyroidism. Nightshade family — contraindicated in nightshade sensitivity. Takes 4-8 weeks for full cortisol-lowering effect.',
    rationale: 'Your cortisol at {value} mcg/dL suggests an elevated stress response impacting hormonal balance. KSM-66 ashwagandha reduces cortisol by 23-30% while supporting testosterone levels, taken in the evening to enhance sleep quality.',

    pricing: {
      manufacturingCost: '$2.50-4.00',
      supplierCost: '$8-13/mo',
      monthlyPrice: '$28/mo',
    },

    status: 'active',
    sortOrder: 17,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 18. DIM (Diindolylmethane)
  // ─────────────────────────────────────────────
  {
    id: 'supp-dim',
    catalogNumber: 18,
    name: 'DIM (Diindolylmethane)',
    ingredients: 'Diindolylmethane 200 mg (from cruciferous vegetable extract), BioPerine 5 mg',
    category: 'supplement',
    subcategory: 'hormone_female',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'estradiol',
        condition: 'above_functional',
        threshold: 200,
        description: 'Estradiol >200 pg/mL (estrogen dominance, female)',
      },
      {
        biomarker: 'estrogen_metabolites_2_16_ratio',
        condition: 'below_functional',
        threshold: 2.0,
        description: '2:16-OH estrone ratio <2.0',
      },
    ],
    targetBiomarkers: ['estradiol', 'estrogen_metabolites_2_16_ratio', 'estrone'],
    targetGoals: ['hormonal', 'body_composition'],
    pairsWith: ['supp-calcium-d-glucarate', 'supp-omega-3-fish-oil', 'supp-fiber-supplement'],

    contraindications: ['hormone_sensitive_cancers_consult_oncologist', 'pregnancy', 'breastfeeding'],
    drugInteractions: ['oral_contraceptives_may_alter_estrogen_metabolism', 'tamoxifen_consult_oncologist', 'cyp1a2_substrates_dim_induces_cyp1a2'],
    allergenFlags: ['cruciferous_vegetable_derived'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['estradiol_at_90_days', 'estrogen_metabolites_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'DIM promotes the 2-hydroxylation pathway of estrogen metabolism, increasing the 2:16 hydroxyestrone ratio. Clinical studies show 200 mg/day shifts estrogen metabolites toward the protective 2-OH pathway within 4-6 weeks, reducing proliferative estrogen signaling.',
    citations: [
      'Dalessandri KM et al. Pilot study: effect of 3,3\'-diindolylmethane supplements on urinary hormone metabolites in postmenopausal women. Nutr Cancer. 2004;50(2):161-167.',
      'Thomson CA et al. Chemopreventive properties of 3,3\'-diindolylmethane in breast cancer. J Carcinog. 2004;3(1):5.',
    ],

    gender: 'female',
    clinicalNotes: 'DIM shifts estrogen metabolism toward the favorable 2-hydroxy pathway (2-OH estrone) away from the proliferative 16-alpha and 4-hydroxy pathways. Pairs synergistically with calcium d-glucarate for complete estrogen detoxification support. Can also benefit males with elevated estradiol but primary indication is female estrogen dominance.',
    rationale: 'Your estradiol at {value} pg/mL suggests estrogen dominance. DIM at 200 mg shifts estrogen metabolism toward the protective 2-hydroxy pathway, reducing symptoms of estrogen excess and supporting healthy hormonal balance.',

    pricing: {
      manufacturingCost: '$2.00-3.50',
      supplierCost: '$7-11/mo',
      monthlyPrice: '$24/mo',
    },

    status: 'active',
    sortOrder: 18,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 19. Calcium D-Glucarate
  // ─────────────────────────────────────────────
  {
    id: 'supp-calcium-d-glucarate',
    catalogNumber: 19,
    name: 'Calcium D-Glucarate',
    ingredients: 'Calcium D-glucarate 500 mg',
    category: 'supplement',
    subcategory: 'hormone_female',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'estradiol',
        condition: 'above_functional',
        threshold: 200,
        description: 'Estradiol >200 pg/mL (estrogen dominance)',
      },
      {
        biomarker: 'ggt',
        condition: 'above_functional',
        threshold: 30,
        description: 'GGT >30 U/L (impaired glucuronidation)',
      },
    ],
    targetBiomarkers: ['estradiol', 'estrone', 'ggt', 'beta_glucuronidase'],
    targetGoals: ['hormonal', 'longevity'],
    pairsWith: ['supp-dim', 'supp-nac', 'supp-fiber-supplement'],

    contraindications: ['pregnancy', 'breastfeeding'],
    drugInteractions: ['oral_contraceptives_may_reduce_efficacy_by_enhancing_clearance', 'hormone_replacement_therapy_may_reduce_efficacy'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['estradiol_at_90_days', 'liver_enzymes_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Calcium d-glucarate inhibits beta-glucuronidase by 57%, preventing enterohepatic recirculation of conjugated estrogens and other glucuronidated toxins. Supports phase III estrogen detoxification alongside DIM (phase I).',
    citations: [
      'Walaszek Z et al. Metabolism, uptake, and excretion of a D-glucaric acid salt and its potential use in cancer prevention. Cancer Detect Prev. 1997;21(2):178-190.',
      'Hanausek M et al. Detoxifying cancer causing agents to prevent cancer. Integr Cancer Ther. 2003;2(2):139-144.',
    ],

    gender: 'female',
    clinicalNotes: 'Inhibits beta-glucuronidase enzyme, preventing re-absorption of conjugated (detoxified) estrogens in the gut. This is the "phase III" of estrogen detoxification. Works synergistically with DIM (phase I estrogen metabolism) for complete estrogen clearance. Also supports glucuronidation of other toxins and carcinogens.',
    rationale: 'Your estrogen markers suggest incomplete estrogen clearance. Calcium d-glucarate blocks the beta-glucuronidase enzyme that recycles estrogen back into circulation, supporting complete estrogen detoxification alongside DIM.',

    pricing: {
      manufacturingCost: '$1.50-3.00',
      supplierCost: '$6-10/mo',
      monthlyPrice: '$22/mo',
    },

    status: 'active',
    sortOrder: 19,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 20. Evening Primrose Oil
  // ─────────────────────────────────────────────
  {
    id: 'supp-evening-primrose-oil',
    catalogNumber: 20,
    name: 'Evening Primrose Oil',
    ingredients: 'Evening primrose oil (Oenothera biennis) 1300 mg, providing GLA (gamma-linolenic acid) 117 mg',
    category: 'supplement',
    subcategory: 'hormone_female',
    tier: 1,
    route: 'oral',
    dosage: '1 softgel daily (PM with food)',
    timing: 'PM_RECOVERY',

    triggers: [
      {
        biomarker: 'progesterone',
        condition: 'below_functional',
        threshold: 10,
        description: 'Progesterone <10 ng/mL (luteal phase)',
      },
      {
        biomarker: 'pms_symptoms',
        condition: 'any_suboptimal',
        description: 'PMS symptoms, breast tenderness, hormonal imbalance',
      },
    ],
    targetBiomarkers: ['progesterone', 'estradiol', 'prostaglandins'],
    targetGoals: ['hormonal'],
    pairsWith: ['supp-dim', 'supp-calcium-d-glucarate', 'supp-magnesium-glycinate'],

    contraindications: ['seizure_disorders_may_lower_seizure_threshold', 'bleeding_disorders', 'scheduled_surgery_discontinue_2_weeks_prior'],
    drugInteractions: ['anticoagulants_increased_bleeding_risk', 'phenothiazines_increased_seizure_risk', 'antiplatelet_agents_increased_bleeding_risk'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['symptom_assessment_at_60_days', 'hormone_panel_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'GLA from evening primrose oil converts to DGLA and anti-inflammatory prostaglandin E1 (PGE1), modulating hormonal sensitivity. Clinical trials show improvement in cyclical mastalgia and PMS symptoms over 2-3 menstrual cycles at 1300 mg/day.',
    citations: [
      'Pruthi S et al. Vitamin E and evening primrose oil for management of cyclical mastalgia. Breast J. 2010;16(2):176-181.',
      'Kashani L et al. Efficacy of evening primrose oil for premenstrual syndrome. J Reprod Med. 2018;63(5-6):272-276.',
    ],

    gender: 'female',
    clinicalNotes: 'GLA (gamma-linolenic acid) is the active component — converts to DGLA and anti-inflammatory prostaglandin E1 (PGE1). Supports progesterone receptor sensitivity and reduces cyclical breast pain. Takes 2-3 menstrual cycles for full effect. Discontinue 2 weeks before any scheduled surgery due to mild anticoagulant effect.',
    rationale: 'Your hormonal profile suggests imbalanced prostaglandin metabolism. Evening primrose oil provides GLA, which converts to anti-inflammatory prostaglandin E1, supporting hormonal balance and reducing cyclical symptoms over 2-3 months.',

    pricing: {
      manufacturingCost: '$2.00-3.50',
      supplierCost: '$6-10/mo',
      monthlyPrice: '$20/mo',
    },

    status: 'active',
    sortOrder: 20,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 21. Tongkat Ali
  // ─────────────────────────────────────────────
  {
    id: 'supp-tongkat-ali',
    catalogNumber: 21,
    name: 'Tongkat Ali',
    ingredients: 'Eurycoma longifolia root extract 400 mg (standardized to 2% eurycomanone, 100:1 extract)',
    category: 'supplement',
    subcategory: 'hormone_male',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'testosterone_free',
        condition: 'below_functional',
        threshold: 15,
        description: 'Free testosterone <15 pg/mL (male)',
      },
      {
        biomarker: 'shbg',
        condition: 'above_functional',
        threshold: 50,
        description: 'SHBG >50 nmol/L (binding excess testosterone)',
      },
      {
        biomarker: 'testosterone_total',
        condition: 'below_functional',
        threshold: 500,
        description: 'Total testosterone <500 ng/dL (male)',
      },
    ],
    targetBiomarkers: ['testosterone_free', 'testosterone_total', 'shbg', 'cortisol_am'],
    targetGoals: ['hormonal', 'sexual_health', 'energy', 'body_composition'],
    pairsWith: ['supp-ashwagandha-ksm66', 'supp-zinc-copper', 'supp-boron'],

    contraindications: ['hormone_sensitive_cancers_prostate_cancer', 'pregnancy', 'breastfeeding'],
    drugInteractions: ['testosterone_replacement_therapy_additive_effect_monitor', 'antihypertensives_may_have_mild_hypotensive_effect', 'hypoglycemic_agents_may_lower_blood_sugar'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['testosterone_panel_at_90_days', 'shbg_at_90_days', 'psa_at_baseline_and_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Tongkat ali (Eurycoma longifolia) at 200-400 mg/day reduces SHBG and increases free testosterone by 15-20% in hypogonadal males. Also reduces cortisol-to-testosterone ratio. A 2022 systematic review confirmed significant improvements in total and free testosterone.',
    citations: [
      'Leisegang K et al. Eurycoma longifolia (Jack) — a systematic review on its traditional uses, chemistry, evidence-based pharmacology and toxicology. J Ethnopharmacol. 2022;298:115658.',
      'Talbott SM et al. Effect of Tongkat Ali on stress hormones and psychological mood state. J Int Soc Sports Nutr. 2013;10(1):28.',
    ],

    gender: 'male',
    clinicalNotes: 'Tongkat ali reduces SHBG, freeing bound testosterone, and may increase total testosterone by 15-20%. Use standardized 100:1 extract (eurycomanone content). Takes 4-8 weeks for full effect. Check PSA at baseline — contraindicated with elevated PSA or prostate cancer history. Mild cortisol-lowering properties synergistic with ashwagandha.',
    rationale: 'Your free testosterone at {value} pg/mL and SHBG at {value} nmol/L suggest excessive testosterone binding. Tongkat ali at 400 mg reduces SHBG and supports free testosterone levels by 15-20%, improving hormonal availability.',

    pricing: {
      manufacturingCost: '$3.00-5.00',
      supplierCost: '$10-15/mo',
      monthlyPrice: '$32/mo',
    },

    status: 'active',
    sortOrder: 21,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 22. Boron
  // ─────────────────────────────────────────────
  {
    id: 'supp-boron',
    catalogNumber: 22,
    name: 'Boron',
    ingredients: 'Boron (as boron glycinate) 6 mg',
    category: 'supplement',
    subcategory: 'hormone_male',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'testosterone_free',
        condition: 'below_functional',
        threshold: 15,
        description: 'Free testosterone <15 pg/mL (male)',
      },
      {
        biomarker: 'estradiol',
        condition: 'above_functional',
        threshold: 40,
        description: 'Estradiol >40 pg/mL (male — elevated)',
      },
    ],
    targetBiomarkers: ['testosterone_free', 'estradiol', 'shbg', 'vitamin_d_25oh'],
    targetGoals: ['hormonal', 'sexual_health'],
    pairsWith: ['supp-tongkat-ali', 'supp-zinc-copper', 'supp-vitamin-d3-k2'],

    contraindications: ['severe_renal_impairment_impaired_excretion'],
    drugInteractions: ['estrogen_replacement_therapy_may_alter_metabolism'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['testosterone_panel_at_90_days', 'estradiol_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Boron at 6 mg/day increases free testosterone by ~25% and decreases estradiol by ~39% within 7 days (Naghii et al. 2011). Also reduces SHBG, inflammatory biomarkers, and enhances vitamin D metabolism. Mechanisms include inhibition of SHBG and aromatase activity.',
    citations: [
      'Naghii MR et al. Comparative effects of daily and weekly boron supplementation on plasma steroid hormones and proinflammatory cytokines. J Trace Elem Med Biol. 2011;25(1):54-58.',
      'Pizzorno L. Nothing Boring About Boron. Integr Med (Encinitas). 2015;14(4):35-48.',
    ],

    gender: 'male',
    clinicalNotes: 'Boron at 6 mg/day increases free testosterone by ~25% and reduces estradiol by ~39% within 1 week (Naghii et al. 2011). Also increases vitamin D utilization and supports bone density. Glycinate form for optimal absorption. One of the most cost-effective hormonal support supplements.',
    rationale: 'Your free testosterone and estradiol levels indicate suboptimal male hormonal balance. Boron at 6 mg daily has been shown to increase free testosterone by 25% and reduce estradiol by 39%, one of the most rapid-acting hormonal interventions available.',

    pricing: {
      manufacturingCost: '$0.50-1.00',
      supplierCost: '$3-5/mo',
      monthlyPrice: '$12/mo',
    },

    status: 'active',
    sortOrder: 22,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 23. Saw Palmetto
  // ─────────────────────────────────────────────
  {
    id: 'supp-saw-palmetto',
    catalogNumber: 23,
    name: 'Saw Palmetto',
    ingredients: 'Saw palmetto berry extract (Serenoa repens) 320 mg, standardized to 85-95% fatty acids and sterols',
    category: 'supplement',
    subcategory: 'hormone_male',
    tier: 1,
    route: 'oral',
    dosage: '1 softgel daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'dht',
        condition: 'above_functional',
        threshold: 50,
        description: 'DHT >50 ng/dL (elevated, male)',
      },
      {
        biomarker: 'psa',
        condition: 'above_functional',
        threshold: 2.0,
        description: 'PSA >2.0 ng/mL (prostate concern, male)',
      },
    ],
    targetBiomarkers: ['dht', 'psa', 'testosterone_free'],
    targetGoals: ['prostate', 'hormonal'],
    pairsWith: ['supp-zinc-copper', 'supp-boron', 'supp-tongkat-ali'],

    contraindications: ['hormone_sensitive_conditions_consult_provider', 'pregnancy_teratogenic_potential', 'breastfeeding'],
    drugInteractions: ['finasteride_additive_5ar_inhibition_do_not_combine', 'dutasteride_additive_5ar_inhibition_do_not_combine', 'anticoagulants_mild_antiplatelet_effect', 'oral_contraceptives_anti_androgenic_effect'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['psa_at_90_days', 'dht_at_90_days_if_available'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Saw palmetto (320 mg standardized extract) inhibits both type I and type II 5-alpha reductase, reducing DHT by 30-40%. Cochrane reviews show improvement in lower urinary tract symptoms (LUTS) associated with BPH. Also used for androgenetic alopecia prevention.',
    citations: [
      'Tacklind J et al. Serenoa repens for benign prostatic hyperplasia. Cochrane Database Syst Rev. 2012;(12):CD001423.',
      'Rossi A et al. Comparitive effectiveness of finasteride vs Serenoa repens in male androgenetic alopecia. J Cutan Aesthet Surg. 2012;5(4):272-276.',
    ],

    gender: 'male',
    clinicalNotes: 'Inhibits 5-alpha reductase types I and II, reducing DHT conversion. 320 mg standardized extract is the clinically studied dose for BPH symptoms and hair loss prevention. Do NOT combine with finasteride or dutasteride (additive 5-alpha reductase inhibition). May artificially lower PSA — inform urologist if patient is on PSA monitoring.',
    rationale: 'Your DHT and/or PSA levels indicate 5-alpha reductase overactivity. Saw palmetto at 320 mg inhibits DHT production, supporting prostate health and reducing androgenic symptoms without the side effects of pharmaceutical 5-alpha reductase inhibitors.',

    pricing: {
      manufacturingCost: '$2.00-3.50',
      supplierCost: '$7-11/mo',
      monthlyPrice: '$24/mo',
    },

    status: 'active',
    sortOrder: 23,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 24. Alpha Lipoic Acid
  // ─────────────────────────────────────────────
  {
    id: 'supp-alpha-lipoic-acid',
    catalogNumber: 24,
    name: 'Alpha Lipoic Acid',
    ingredients: 'R-Alpha lipoic acid 600 mg (stabilized R-form)',
    category: 'supplement',
    subcategory: 'antioxidant_detox',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM, 30 min before food for best absorption)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'fasting_insulin',
        condition: 'above_functional',
        threshold: 7,
        description: 'Fasting insulin >7 uIU/mL',
      },
      {
        biomarker: 'fasting_glucose',
        condition: 'above_functional',
        threshold: 95,
        description: 'Fasting glucose >95 mg/dL',
      },
      {
        biomarker: 'hba1c',
        condition: 'above_functional',
        threshold: 5.5,
        description: 'HbA1c >5.5%',
      },
    ],
    targetBiomarkers: ['fasting_insulin', 'fasting_glucose', 'hba1c', 'oxidative_stress_markers'],
    targetGoals: ['longevity', 'cardiovascular', 'cognition'],
    pairsWith: ['supp-berberine', 'supp-nac', 'supp-coq10-ubiquinol'],

    contraindications: ['thiamine_deficiency_ala_may_worsen', 'heavy_alcohol_use_thiamine_depletion_risk'],
    drugInteractions: ['insulin_increased_hypoglycemia_risk_monitor', 'sulfonylureas_increased_hypoglycemia_risk', 'levothyroxine_separate_by_4hrs', 'cisplatin_may_reduce_efficacy'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['fasting_glucose_at_90_days', 'fasting_insulin_at_90_days', 'hba1c_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'ALA is both water- and fat-soluble, capable of regenerating vitamins C and E and raising intracellular glutathione. The R-form is 40-50% more bioavailable than racemic ALA. 600 mg/day improves insulin sensitivity and reduces neuropathic pain in diabetic patients (ALADIN/SYDNEY trials).',
    citations: [
      'Ziegler D et al. Treatment of symptomatic diabetic polyneuropathy with alpha-lipoic acid (ALADIN III study). Diabetes Care. 1999;22(8):1296-1301.',
      'Shay KP et al. Alpha-lipoic acid as a dietary supplement: molecular mechanisms and therapeutic potential. Biochim Biophys Acta. 2009;1790(10):1149-1160.',
    ],

    gender: 'any',
    clinicalNotes: 'Use R-ALA form (not racemic) for superior bioavailability. Take on empty stomach 30 min before meals. Potent metal chelator — useful in heavy metal detox protocols. Can lower blood sugar significantly in diabetics — adjust insulin/sulfonylurea doses accordingly. Check thiamine status in alcoholic patients before starting.',
    rationale: 'Your insulin and glucose markers indicate metabolic stress. Alpha lipoic acid at 600 mg (R-form) improves insulin sensitivity, regenerates other antioxidants (vitamins C, E, glutathione), and provides neuroprotective support.',

    pricing: {
      manufacturingCost: '$3.00-5.00',
      supplierCost: '$10-15/mo',
      monthlyPrice: '$32/mo',
    },

    status: 'active',
    sortOrder: 24,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 25. Chromium Picolinate
  // ─────────────────────────────────────────────
  {
    id: 'supp-chromium-picolinate',
    catalogNumber: 25,
    name: 'Chromium Picolinate',
    ingredients: 'Chromium (as chromium picolinate) 1000 mcg',
    category: 'supplement',
    subcategory: 'weight_metabolic',
    tier: 1,
    route: 'oral',
    dosage: '1 cap daily (AM with food)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'fasting_insulin',
        condition: 'above_functional',
        threshold: 7,
        description: 'Fasting insulin >7 uIU/mL',
      },
      {
        biomarker: 'fasting_glucose',
        condition: 'above_functional',
        threshold: 95,
        description: 'Fasting glucose >95 mg/dL',
      },
      {
        biomarker: 'hba1c',
        condition: 'above_functional',
        threshold: 5.5,
        description: 'HbA1c >5.5%',
      },
    ],
    targetBiomarkers: ['fasting_insulin', 'fasting_glucose', 'hba1c', 'homa_ir'],
    targetGoals: ['body_composition', 'cardiovascular'],
    pairsWith: ['supp-berberine', 'supp-alpha-lipoic-acid', 'supp-fiber-supplement'],

    contraindications: ['chromate_allergy', 'renal_insufficiency_impaired_excretion'],
    drugInteractions: ['insulin_increased_hypoglycemia_risk', 'metformin_additive_glucose_lowering', 'levothyroxine_separate_by_4hrs', 'nsaids_may_increase_chromium_absorption'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['fasting_glucose_at_90_days', 'fasting_insulin_at_90_days', 'hba1c_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Chromium enhances insulin receptor signaling via chromodulin activation. 1000 mcg/day as picolinate form reduces fasting glucose, improves insulin sensitivity, and reduces carbohydrate cravings. Meta-analyses show modest but consistent glucose and HbA1c reductions in insulin-resistant individuals.',
    citations: [
      'Balk EM et al. Effect of chromium supplementation on glucose metabolism and lipids. Diabetes Care. 2007;30(8):2154-2163.',
      'Anton SD et al. Effects of chromium picolinate on food intake and satiety. Diabetes Technol Ther. 2008;10(5):405-412.',
    ],

    gender: 'any',
    clinicalNotes: 'Picolinate form has the best absorption and most clinical evidence. 1000 mcg is the therapeutic dose for insulin resistance — lower doses (200 mcg) are insufficient for metabolic benefit. Reduces carbohydrate cravings via insulin sensitization. Synergistic with berberine and ALA for insulin resistance. Monitor renal function in CKD patients.',
    rationale: 'Your fasting insulin at {value} uIU/mL indicates insulin resistance. Chromium picolinate at 1000 mcg enhances insulin receptor sensitivity via chromodulin activation, shown to reduce cravings and improve glucose utilization.',

    pricing: {
      manufacturingCost: '$0.75-1.50',
      supplierCost: '$3-6/mo',
      monthlyPrice: '$14/mo',
    },

    status: 'active',
    sortOrder: 25,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 26. Vitamin E (Mixed Tocopherols)
  // ─────────────────────────────────────────────
  {
    id: 'supp-vitamin-e-mixed-tocopherols',
    catalogNumber: 26,
    name: 'Vitamin E (Mixed Tocopherols)',
    ingredients: 'Mixed tocopherols (d-alpha, d-beta, d-gamma, d-delta tocopherols) 400 IU, plus mixed tocotrienols',
    category: 'supplement',
    subcategory: 'antioxidant_detox',
    tier: 1,
    route: 'oral',
    dosage: '1 softgel daily (AM with fat-containing meal)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'oxidized_ldl',
        condition: 'above_functional',
        threshold: 60,
        description: 'Oxidized LDL >60 U/L',
      },
      {
        biomarker: 'vitamin_e',
        condition: 'below_functional',
        threshold: 12,
        description: 'Serum vitamin E <12 mg/L',
      },
    ],
    targetBiomarkers: ['oxidized_ldl', 'vitamin_e', 'lipid_peroxidation'],
    targetGoals: ['cardiovascular', 'longevity'],
    pairsWith: ['supp-vitamin-c', 'supp-omega-3-fish-oil', 'supp-coq10-ubiquinol'],

    contraindications: ['vitamin_k_deficiency_potentiates_bleeding', 'retinitis_pigmentosa_high_doses'],
    drugInteractions: ['warfarin_increased_bleeding_risk_monitor_inr', 'antiplatelet_agents_aspirin_increased_bleeding', 'statins_may_reduce_vitamin_e_benefit', 'chemotherapy_antioxidant_interaction_consult_oncologist'],
    allergenFlags: ['soy_derived_possible'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['inr_if_on_anticoagulants', 'lipid_panel_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Mixed tocopherols (not alpha-tocopherol alone) provide comprehensive lipid-phase antioxidant protection. Gamma-tocopherol is the primary dietary form and traps reactive nitrogen species that alpha-tocopherol cannot. 400 IU mixed tocopherols reduces oxidized LDL and lipid peroxidation.',
    citations: [
      'Jiang Q et al. Gamma-tocopherol and its major metabolite, in contrast to alpha-tocopherol, inhibit COX activity. Proc Natl Acad Sci. 2000;97(21):11494-11499.',
      'Devaraj S et al. Gamma-tocopherol supplementation alone and in combination with alpha-tocopherol alters biomarkers of oxidative stress. Free Radic Biol Med. 2008;44(6):1203-1208.',
    ],

    gender: 'any',
    clinicalNotes: 'MUST use mixed tocopherols — isolated alpha-tocopherol depletes gamma-tocopherol and showed harm in SELECT trial. Gamma-tocopherol is the critical anti-inflammatory form (traps peroxynitrite). Caution with blood thinners — vitamin E has mild anticoagulant effect. Discontinue 2 weeks before surgery. Take with fat for absorption.',
    rationale: 'Your lipid oxidation markers suggest increased oxidative damage to LDL particles. Mixed tocopherols (400 IU) provide comprehensive fat-soluble antioxidant protection, with gamma-tocopherol targeting reactive nitrogen species that alpha alone cannot neutralize.',

    pricing: {
      manufacturingCost: '$2.00-4.00',
      supplierCost: '$7-12/mo',
      monthlyPrice: '$26/mo',
    },

    status: 'active',
    sortOrder: 26,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 27. Digestive Enzymes
  // ─────────────────────────────────────────────
  {
    id: 'supp-digestive-enzymes',
    catalogNumber: 27,
    name: 'Digestive Enzymes',
    ingredients: 'Protease blend 80,000 HUT, lipase 6,000 FIP, amylase 24,000 DU, cellulase 2,000 CU, lactase 1,000 ALU, bromelain 500 GDU, ox bile extract 100 mg',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'oral',
    dosage: '1 cap with each meal (AM, midday, PM)',
    timing: 'AM_ENERGY',
    additionalTimings: ['MIDDAY_ABSORPTION', 'PM_RECOVERY'],

    triggers: [
      {
        biomarker: 'nutrient_absorption',
        condition: 'any_suboptimal',
        description: 'Poor nutrient absorption, bloating, or GI symptoms after meals',
      },
      {
        biomarker: 'vitamin_d_25oh',
        condition: 'below_functional',
        threshold: 40,
        description: 'Persistent low fat-soluble vitamins despite supplementation',
      },
    ],
    targetBiomarkers: ['nutrient_absorption', 'fat_soluble_vitamins'],
    targetGoals: ['energy', 'immune'],
    pairsWith: ['supp-probiotic', 'supp-fiber-supplement'],

    contraindications: ['acute_pancreatitis', 'porcine_allergy_if_pancreatin_based'],
    drugInteractions: ['acarbose_may_counteract_starch_blocking_effect', 'warfarin_bromelain_may_increase_bleeding_risk'],
    allergenFlags: ['porcine_derived_ox_bile'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['symptom_assessment_at_30_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Supplemental digestive enzymes improve macronutrient absorption in patients with insufficient endogenous enzyme production (common after age 40, with PPI use, or pancreatic insufficiency). Broad-spectrum formulations addressing proteins, fats, and carbohydrates provide the most comprehensive support.',
    citations: [
      'Ianiro G et al. Digestive enzyme supplementation in gastrointestinal diseases. Curr Drug Metab. 2016;17(2):187-193.',
      'Roxas M. The role of enzyme supplementation in digestive disorders. Altern Med Rev. 2008;13(4):307-314.',
    ],

    gender: 'any',
    clinicalNotes: 'Take at the START of each meal for optimal mixing with food. Most critical for patients over 40, PPI users (reduced acid impairs protein digestion), or those with gallbladder removal (ox bile component critical). If persistently low fat-soluble vitamins despite supplementation, suspect absorption issue. Ox bile supports fat digestion in cholecystectomy patients.',
    rationale: 'Your nutrient absorption markers and/or GI symptoms suggest suboptimal digestive function. A broad-spectrum enzyme complex taken with each meal ensures proper breakdown and absorption of proteins, fats, and carbohydrates, maximizing the benefit of your other supplements.',

    pricing: {
      manufacturingCost: '$3.00-5.00',
      supplierCost: '$10-15/mo',
      monthlyPrice: '$30/mo',
    },

    status: 'active',
    sortOrder: 27,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 28. Melatonin
  // ─────────────────────────────────────────────
  {
    id: 'supp-melatonin',
    catalogNumber: 28,
    name: 'Melatonin',
    ingredients: 'Melatonin 3 mg sustained-release',
    category: 'supplement',
    subcategory: 'foundation',
    tier: 1,
    route: 'sublingual',
    dosage: '1 tab nightly (PM, 30-60 min before bed, dissolve under tongue)',
    timing: 'PM_RECOVERY',

    triggers: [
      {
        biomarker: 'sleep_quality',
        condition: 'any_suboptimal',
        description: 'Sleep onset >30 min, sleep quality issues, circadian disruption',
      },
      {
        biomarker: 'cortisol_pm',
        condition: 'above_functional',
        threshold: 5,
        description: 'Evening cortisol >5 mcg/dL (disrupted circadian rhythm)',
      },
    ],
    targetBiomarkers: ['sleep_quality', 'cortisol_pm', 'circadian_rhythm'],
    targetGoals: ['sleep', 'longevity', 'immune'],
    pairsWith: ['supp-magnesium-glycinate', 'supp-ashwagandha-ksm66'],

    contraindications: ['autoimmune_conditions_immunostimulatory', 'seizure_disorders_conflicting_data', 'pregnancy', 'breastfeeding'],
    drugInteractions: ['fluvoxamine_increases_melatonin_levels_10_20x', 'immunosuppressants_melatonin_is_immunostimulatory', 'anticoagulants_may_increase_bleeding_risk', 'antihypertensives_additive_blood_pressure_lowering', 'sedatives_benzodiazepines_additive_sedation'],
    allergenFlags: [],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['sleep_quality_assessment_at_30_days'],

    evidenceLevel: 'strong',
    evidenceSummary: 'Melatonin 0.5-5 mg reduces sleep onset latency by 7-12 minutes and improves overall sleep quality. Sustained-release formulations better address sleep maintenance. Also has potent antioxidant properties (scavenges hydroxyl radicals) and immunomodulatory effects. Production declines significantly after age 40.',
    citations: [
      'Ferracioli-Oda E et al. Meta-analysis: melatonin for the treatment of primary sleep disorders. PLoS One. 2013;8(5):e63773.',
      'Reiter RJ et al. Melatonin as an antioxidant: biochemical mechanisms and pathophysiological implications in humans. Acta Biochim Pol. 2003;50(4):1129-1146.',
    ],

    gender: 'any',
    clinicalNotes: 'Start at 3 mg sustained-release — lower doses (0.5-1 mg) for pure circadian reset, higher doses (3-5 mg) for sleep onset + maintenance. Sublingual delivery bypasses first-pass metabolism for faster onset. CRITICAL: fluvoxamine increases melatonin levels 10-20x — reduce dose to 0.5 mg or avoid. Natural production declines ~10% per decade after age 20. Sustained-release addresses both sleep onset and maintenance.',
    rationale: 'Your sleep markers and/or circadian pattern suggest suboptimal melatonin signaling. Sustained-release melatonin at 3 mg (sublingual) addresses both sleep onset and maintenance while providing antioxidant and immune support during the overnight repair window.',

    pricing: {
      manufacturingCost: '$0.50-1.00',
      supplierCost: '$3-5/mo',
      monthlyPrice: '$12/mo',
    },

    status: 'active',
    sortOrder: 28,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 29. Collagen Peptides
  // ─────────────────────────────────────────────
  {
    id: 'supp-collagen-peptides',
    catalogNumber: 29,
    name: 'Collagen Peptides',
    ingredients: 'Hydrolyzed bovine collagen peptides (Types I & III) 10 g, vitamin C 50 mg',
    category: 'supplement',
    subcategory: 'beauty',
    tier: 1,
    route: 'oral',
    dosage: '1 scoop (10g) daily (AM, mixed in coffee/smoothie)',
    timing: 'AM_ENERGY',

    triggers: [
      {
        biomarker: 'skin_health',
        condition: 'any_suboptimal',
        description: 'Skin elasticity concerns, joint discomfort, hair thinning',
      },
    ],
    targetBiomarkers: ['skin_health', 'joint_health', 'hair_health'],
    targetGoals: ['longevity', 'body_composition'],
    pairsWith: ['supp-vitamin-c', 'supp-omega-3-fish-oil'],

    contraindications: ['bovine_allergy', 'histamine_intolerance_some_collagen_sources_high_histamine'],
    drugInteractions: [],
    allergenFlags: ['bovine_derived', 'fish_derived_if_marine_collagen'],
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['subjective_assessment_at_90_days'],

    evidenceLevel: 'moderate',
    evidenceSummary: 'Hydrolyzed collagen peptides at 10 g/day improve skin elasticity (+15% at 8 weeks), reduce joint pain (24-week studies), and increase nail growth rate. Types I and III target skin, hair, and connective tissue. Vitamin C co-administration supports endogenous collagen synthesis via prolyl hydroxylase.',
    citations: [
      'Proksch E et al. Oral supplementation of specific collagen peptides has beneficial effects on human skin physiology. Skin Pharmacol Physiol. 2014;27(1):47-55.',
      'Clark KL et al. 24-Week study on the use of collagen hydrolysate as a dietary supplement in athletes with activity-related joint pain. Curr Med Res Opin. 2008;24(5):1485-1496.',
    ],

    gender: 'any',
    clinicalNotes: 'Hydrolyzed peptides (2-5 kDa molecular weight) have >90% absorption vs intact collagen. Types I & III are most relevant for skin, hair, and joints. Include vitamin C (cofactor for collagen synthesis). Best taken on empty stomach or with low-protein meal to avoid amino acid competition. Results visible at 8-12 weeks for skin, 12-24 weeks for joints.',
    rationale: 'Collagen production declines ~1% per year after age 25. At 10 g daily of hydrolyzed peptides (with vitamin C), you provide the specific amino acids (glycine, proline, hydroxyproline) needed to support skin elasticity, joint integrity, and hair strength.',

    pricing: {
      manufacturingCost: '$3.00-6.00',
      supplierCost: '$10-16/mo',
      monthlyPrice: '$32/mo',
    },

    status: 'active',
    sortOrder: 29,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },
];
